import { TypedDocumentNode, gql } from '@apollo/client';
import { Navigate } from 'react-router-dom';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { FOOTBALL_SCOUTING_CENTER } from '@sorare/core/src/constants/__generated__/routes';
import { withRouteQuery } from '@sorare/core/src/lib/navigation/withRouteQuery';

import { PaginatedPlayerRows } from '@sorare/marketplace/src/components/benchObject/PaginatedPlayerRows';
import { PageTemplate } from '@sorare/marketplace/src/components/market/PageTemplate';
import useDefaultFilters from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { scoutingCenterMarketFilters } from '@sorare/marketplace/src/searchCards/football/cardFilters';

import { ScoutingCenterHeader } from 'components/scoutingCenter/ScoutingCenterHeader';
import { FootballPlayerSearch } from 'components/searchCards/FootballPlayerSearch';
import { SORTS } from 'lib/sorts';

import type {
  FootballScoutingCenterSo5CompetitionPage,
  FootballScoutingCenterSo5CompetitionPageVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const FOOTBALL_SCOUTING_CENTER_SO5_COMPETITION_PAGE_QUERY = gql`
  query FootballScoutingCenterSo5CompetitionPage($slug: String!) {
    so5 {
      so5Competition(slug: $slug) {
        slug
        algoliaFilters
        scoutable
        competitions {
          slug
          ...ScoutingCenterHeader_competition
        }
        ...ScoutingCenterHeader_so5Competition
      }
    }
  }
  ${ScoutingCenterHeader.fragments.so5Competition}
  ${ScoutingCenterHeader.fragments.competition}
` as TypedDocumentNode<
  FootballScoutingCenterSo5CompetitionPage,
  FootballScoutingCenterSo5CompetitionPageVariables
>;

const ScoutingCenterSo5CompetitionPage = withRouteQuery(
  ({ queryResult }) => {
    const { data } = queryResult;

    const { so5Competition } = data.so5;

    const defaultFilters = useDefaultFilters({
      extraFilters: so5Competition.algoliaFilters,
    });

    if (!so5Competition.scoutable)
      return <Navigate to={FOOTBALL_SCOUTING_CENTER} replace />;

    return (
      <PageTemplate showBack>
        <Vertical gap={2}>
          <ScoutingCenterHeader
            so5Competition={so5Competition}
            competitions={so5Competition.competitions}
          />
          <div>
            <FootballPlayerSearch
              sorts={SORTS}
              defaultSort={SORTS[0]}
              cardFilters={scoutingCenterMarketFilters}
              analyticsTags={['Scouting-center', 'Football']}
              topic={{ type: 'scoutingCenter', label: '' }}
              GridComponent={PaginatedPlayerRows}
              attributesToRetrieve={['slug']}
              defaultFilters={defaultFilters}
            />
          </div>
        </Vertical>
      </PageTemplate>
    );
  },
  {
    query: FOOTBALL_SCOUTING_CENTER_SO5_COMPETITION_PAGE_QUERY,
    queryOptions: ({ slug }: PageParams) => ({
      variables: {
        slug,
      },
    }),
  }
);

export default ScoutingCenterSo5CompetitionPage;
